/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Options from '@linc-technologies/linced-common/enums/options';
import {
  getCurrentMilestone,
  generateStudentProgressData,
} from '@linc-technologies/linced-common/utils/progressions';

export default class EditController extends Controller {
  @action
  setNumMilestonesToShow(value) {
    this.numMilestonesToShow = value;
  }

  @service
  colours;

  @service
  session;

  @service
  dates;

  @service
  options;

  ProgressionType = ProgressionType;
  timestampFormat = 'll [at] LT';
  type = ProgressionType.PROGRESSION;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @computed('session.activeSchool')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  @optionAlias(Options.CHARTS_SHOW_ALL_COLOURS)
  showAllColours;

  @optionAlias(Options.CHARTS_SHOW_Y_AXIS_LABELS)
  showYAxisLabels;

  @optionAlias(Options.CHARTS_HIDE_TOOL_TIP)
  hideTooltip;

  @computed('model.levels.@each.sequence')
  get levels() {
    return this.model.levels
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @alias('model.subject.milestones')
  milestones;

  @computed(
    'milestones.[]',
    'model.person.{weeksAtSchool,monthsAtSchool,currentYearLevel}',
  )
  get currentMilestone() {
    return getCurrentMilestone(
      this.milestones,
      this.model.person,
      this.dates.rolloverMonthDate,
    );
  }

  @computed('currentMilestone', 'milestones.[]')
  get currentMilestoneIndex() {
    return this.milestones.indexOf(this.currentMilestone);
  }

  @computed('milestones.@each.id', 'model.progressions.@each.milestoneId')
  get maxMilestoneIndex() {
    return this.model.progressions.reduce((maxIndex, progression) => {
      let milestone = this.milestones.find(
        (item) => item.id === progression.milestone_id,
      );
      let index = this.milestones.indexOf(milestone);
      return Math.max(maxIndex, index);
    }, -1);
  }

  @tracked numMilestonesToShow = 4;

  @computed(
    'currentMilestoneIndex',
    'maxMilestoneIndex',
    'numMilestonesToShow',
    'milestones.[]',
  )
  get sliderMilestones() {
    // Usually, we want to show up to the learner's current milestone.
    let currentIndex = this.currentMilestoneIndex;
    // If they have progressions for "future" milestones, we want to show sliders for those.
    let maxIndex = Math.max(this.currentMilestoneIndex, this.maxMilestoneIndex);

    if (maxIndex >= 0) {
      // Min index is calculated from current rather than max, so that we don't end up cutting off
      // above the current milestone if they have far-future progressions already.
      let minIndex = Math.max(0, currentIndex - this.numMilestonesToShow + 1);
      return this.milestones.slice(minIndex, maxIndex + 1).reverse();
    }

    return [];
  }

  @computed(
    'sliderMilestones.length',
    'currentMilestoneIndex',
    'maxMilestoneIndex',
  )
  get moreMilestonesAvailable() {
    let maxIndex = Math.max(this.currentMilestoneIndex, this.maxMilestoneIndex);
    return maxIndex - this.sliderMilestones.length >= 0;
  }

  @computed('model.{milestones.[],levels.[],expectations.[],progressions.[]}')
  get progressions() {
    return generateStudentProgressData(
      this.model.expectations,
      this.milestones,
      this.levels,
      this.model.progressions,
      this.showAllColours,
    );
  }

  @action
  updateRecords() {
    this.model.progressions.update();
  }

  @action
  updateChangelog() {
    this.model.changelog.update();
  }

  reset() {
    this.numMilestonesToShow = 4;
  }
}
